/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-04-28 10:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, TextContainer, FormLayout, ChoiceList, Banner } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import { RenderDateRangePicker } from 'src/components/views/analytics-v2/render-date-range-picker';
import { getUTCDate } from 'src/helpers/date-time-helper';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import CollectionAnalyticActionList from 'src/components/views/analytics-v2/collection-analytic-action-list';
import { FEATURES, COLLECTION_ANALYTIC_METRIC } from 'src/constants/analytics';
type Props = {
  active: boolean,
  optionCollections: Array<Object>,
  collection?: Object,
  fromDate?: Date,
  toDate?: Date,
  handleChangeModal: Function,
  appLanguage: string,
  metric?: string,
  title: string,
  feature?: string
};

type ComposedProps = Props & WithI18nProps;

type State = {
  collection: Object,
  fromDate: Date,
  toDate: Date,
  fileFormat: Array<string>,
  showMessage: boolean
};
class ExportModal extends Component<ComposedProps, State> {
  timerShowWarning = undefined;
  state = {
    currenCollectionId: 'all',
    fromDate: new Date(),
    toDate: new Date(),
    fileFormat: ['csv'],
    showMessage: false,
    currenCollection: 'all'
  };

  componentWillReceiveProps(nextProps) {
    const { exporting, collection, fromDate, toDate } = nextProps;

    if (exporting) {
      this.timerShowWarning = setTimeout(
        () => this.setState({ ...this.state, showMessage: true }),
        15000
      );
    } else if (this.timerShowWarning) {
      clearTimeout(this.timerShowWarning);
      this.timerShowWarning = null;
      this.setState({ ...this.state, showMessage: false });
    } else {
      this.setState({ ...this.state, collection, fromDate, toDate, showMessage: false });
    }
  }

  handleSelectCollection = collectionId => {
    this.setState({ currenCollectionId: collectionId });
  };

  render() {
    const {
      active,
      handleChangeModal,
      exporting,
      appLanguage,
      allowTrialAddon,
      title,
      limitDays,
      enabledSubscriptionV4,
      openReviewAddonModal,
      i18n,
      feature,
      collections,
      disableLockUnLock,
      metric
    } = this.props;
    const { fromDate, toDate, fileFormat, showMessage, currenCollectionId } = this.state;

    return (
      <Modal
        open={active}
        onClose={handleChangeModal}
        title={title}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.export'),
          loading: exporting,
          onAction: this.handleExportData
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: handleChangeModal
          }
        ]}>
        <Modal.Section>
          <div className="BC-Analytics__Export-Modal">
            <TextContainer>
              <FormLayout>
                {exporting && showMessage && (
                  <Banner
                    status="warning"
                    title={i18n.translate('AdminHTML.Analytics.ExportModal.banner_Title')}>
                    {i18n.translate('AdminHTML.Analytics.ExportModal.banner_Content')}
                  </Banner>
                )}

                {feature === FEATURES.COLLECTION &&
                  metric !== COLLECTION_ANALYTIC_METRIC.OVERVIEW &&
                  metric !== COLLECTION_ANALYTIC_METRIC.COLLECTION_PERFORMANCES && (
                    <div>
                      <RenderLabelTooltip
                        label={i18n.translate(
                          'AdminHTML.Analytics.CollectionAnalytics.selectCollection'
                        )}
                      />
                      <CollectionAnalyticActionList
                        collections={collections}
                        handleSelectCollection={this.handleSelectCollection}
                        currenCollectionId={currenCollectionId}></CollectionAnalyticActionList>
                    </div>
                  )}

                {/* {feature === FEATURES.COLLECTION && (
                  <React.Fragment>
                    <div className="filter-collection">
                      <RenderSelect2
                        name="collection"
                        label={i18n.translate('AdminHTML.Analytics.ExportModal.collection')}
                        options={optionCollections}
                        value={collection}
                        onChange={value => this.changeValue('collection', value)}
                        updateStyle={styleSelect}
                        isDisabled={exporting}
                      />
                    </div>
                  </React.Fragment>
                )} */}
                <div>
                  <RenderLabelTooltip
                    label={i18n.translate('AdminHTML.Analytics.ExportModal.dateRange')}
                  />
                  <RenderDateRangePicker
                    feature={feature}
                    fromDate={fromDate}
                    toDate={toDate}
                    onChooseDate={this.onChooseDate}
                    appLanguage={appLanguage}
                    showCompare={false}
                    disabled={exporting}
                    allowTrialAddon={allowTrialAddon}
                    limitDays={limitDays}
                    enabledSubscriptionV4={enabledSubscriptionV4}
                    openReviewAddonModal={openReviewAddonModal}
                    disableLockUnLock={disableLockUnLock}
                  />
                </div>
                <ChoiceList
                  title={i18n.translate('AdminHTML.Analytics.ExportModal.fileFormat')}
                  disabled={exporting}
                  choices={[
                    {
                      label: i18n.translate('AdminHTML.Analytics.Constants.fileFormat_Type01'),
                      value: 'csv'
                    },
                    {
                      label: i18n.translate('AdminHTML.Analytics.Constants.fileFormat_Type02'),
                      value: 'excel'
                    }
                  ]}
                  selected={fileFormat}
                  onChange={value => this.changeValue('fileFormat', value)}
                />
              </FormLayout>
            </TextContainer>
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  changeValue = (name, value) => {
    this.setState({ [name]: value });
  };

  onChooseDate = (selectedDate, compare) => {
    let { start, end } = selectedDate;

    this.changeValue('fromDate', start);
    this.changeValue('toDate', end);
  };

  handleExportData = async () => {
    const { fromDate, toDate, fileFormat, currenCollectionId } = this.state;
    const { exportData, collections, i18n } = this.props;
    const collection = collections.find(collection => collection.id === currenCollectionId) || {
      title: i18n.translate('AdminHTML.Analytics.CollectionAnalytics.allCollections'),
      id: 'all'
    };

    const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
    const toDateUTC = getUTCDate(toDate, 24, 0, 0);

    await exportData(collection.id, collection.title, fromDateUTC, toDateUTC, fileFormat);
  };
}

const mapStateToProps = (state, props) => {
  return {
    exporting: _.get(state.analyticsV2, 'exporting', false),

    // List collections
    collections: _.get(state.analyticsV2, 'collections', [])
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  exportData: (collectionId, collectionLabel, fromDate, toDate, fileFormat) =>
    dispatch(
      AnalyticsActions.exportData(
        collectionId,
        collectionLabel,
        fromDate,
        toDate,
        fileFormat,
        ownProps.feature,
        ownProps.metric,
        ownProps.i18n
      )
    ) // : data => dispatch(AnalyticsActions.exportData(data, ownProps.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ExportModal));
